import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Merges Tailwind class names, resolving any conflicts.
 *
 * @param inputs - An array of class names to merge.
 * @returns A string of merged and optimized class names.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Resolves a translated string from a translation object or JSON string.
 * Falls back to Italian ('it') or the first available non-empty translation if the requested language is missing or empty.
 */
export function getTranslatedName(
  value: any,
  currentLang: string = "it",
): string {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  let translations = value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        translations = JSON.parse(trimmed);
      } catch {
        return value;
      }
    } else {
      return value;
    }
  }

  if (typeof translations === "object" && translations !== null) {
    let rawLang = (currentLang || "it").toLowerCase();
    let lang = rawLang === "en" || rawLang.startsWith("en-") ? "gb" : rawLang;
    let nameObj =
      translations?.name ?? translations?.description ?? translations;

    if (typeof nameObj === "string") {
      return nameObj;
    }

    if (typeof nameObj === "object" && nameObj !== null) {
      const langVal = nameObj?.[lang];
      if (langVal && typeof langVal === "string" && langVal.trim() !== "") {
        return langVal.trim();
      }

      if (rawLang !== lang) {
        const rawVal = nameObj?.[rawLang];
        if (rawVal && typeof rawVal === "string" && rawVal.trim() !== "") {
          return rawVal.trim();
        }
      }

      const itVal = nameObj?.["it"];
      if (itVal && typeof itVal === "string" && itVal.trim() !== "") {
        return itVal.trim();
      }

      const enVal = nameObj?.["en"] || nameObj?.["gb"];
      if (enVal && typeof enVal === "string" && enVal.trim() !== "") {
        return enVal.trim();
      }

      const anyVal = Object.values(nameObj).find(
        (v) => typeof v === "string" && v.trim() !== "",
      );
      if (anyVal) {
        return String(anyVal).trim();
      }
      return "";
    }
    return "";
  }

  return typeof value === "string" ? value : "";
}
